import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { Contact, ApiGetContactsOptions } from '@models/contact/contact';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { ContactField } from '@models/contact/field';
import { ContactGroup, ContactGropsSearchFilter } from '@models/contact/group';
import { EmailCampaign } from '@models/email/email-campaign';
import { User } from '@models/user';
import { NgbModalRef, NgbModal, NgbModalOptions } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { AtlasToastService } from '@services/atlas-toast.service';
import { ContactService } from '@services/contact.service';
import { EmailCampaignListService } from '@services/email-campaign-list.service';
import { UserService } from '@services/user.service';
import { AtlasGridOptions } from '@ui/components/atlas-grid/altas-grid-options.model';
import { AtlasGridComponent } from '@ui/components/atlas-grid/atlas-grid.component';
import { ButtonGroupRendererComponent } from '@ui/components/atlas-grid/renderer/buttongroup-renderer.component';
import { CheckboxCellComponent } from '@ui/components/atlas-grid/renderer/checkbox-cell-renderer.component';
import { GridHeaderSelectComponent } from '@ui/components/atlas-grid/renderer/grid-header-select.component';
import { AtlasGridHtmlRendererComponent } from '@ui/components/atlas-grid/renderer/html-renderer.component';
import { enableEmailInExport, validateFormGroup } from '@utils/utils';
import { RowSelectedEvent, GridReadyEvent } from 'ag-grid-community';
import { Observable, forkJoin } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { SelectEmailCampaignModalComponent } from '../select-email-campaign-modal/select-email-campaign-modal.component';
import { LoaderService } from '@services/loader.service';

// import {faEdit, faEye, faTrash} from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'wsb-contacts-list',
  templateUrl: './contacts-list.component.html',
  styleUrls: ['./contacts-list.component.scss'],
  animations: [
    trigger('gridIsLoaded', [
      state('hide', style({ opacity: 0 })),
      state('show', style({ opacity: 1 })),
      transition('hide => show', animate('150ms', style({ opacity: 1 }))),
      transition('show => hide', animate('150ms', style({ opacity: 0 })))
    ])
  ],
  standalone: false
})
export class ContactsListComponent implements OnInit, OnDestroy {
  @Input() page;
  @ViewChild('pageDashboard', { static: true }) $pageDashboard;
  @ViewChild('modalDelete') modalDelete;
  @ViewChild('modalDeleteMultiple') modalDeleteMultiple;
  @ViewChild('modalExport') modalExport;
  @ViewChild('modalAddRemoveCreateGroup') modalAddRemoveCreateGroup;
  @ViewChild('grid') grid: AtlasGridComponent;
  @ViewChild('groupgrid') groupgrid: AtlasGridComponent;
  @ViewChild('modalSuccessExport') modalSuccessExport;

  modalSuccessExportModal: NgbModalRef;
  modalDeleteModal: NgbModalRef;
  modalDeleteMultipleModal: NgbModalRef;
  modalExportModal: NgbModalRef;
  modalAddRemoveCreateGroupModal: NgbModalRef;
  user: User | null;
  contactToDelete: Contact | null;
  isLoaderActive = true;

  tableColumnDefs: any = [];
  tableGridOptions = null;

  badgeLabel: string;

  bulkOption;

  subAccountFirstColumns: any = [];
  superAccountFirstColumn: any = [];
  tableData = null;
  components = {
    buttonGroupRenderer: ButtonGroupRendererComponent,
    htmlRenderer: AtlasGridHtmlRendererComponent
  };
  tableLoading: boolean;
  paginationPageSize = 10;
  tableGroupColumnDefs = null;
  tableGroupGridOptions = null;
  tableGroupGridApi = null;
  frameworkGroupComponents = {
    buttonGroupRenderer: ButtonGroupRendererComponent
  };
  tableGroupLoading: boolean;
  exportForm: UntypedFormGroup = null;
  createGroupForm: UntypedFormGroup = null;
  groupOperation: null;

  backupData;

  enableEmailInExport: boolean;
  emailCampaignList;
  isSelectedAll = false;
  selectedList = null;
  gridAnimationStateGroup = 'hide';
  gridAnimationState = 'hide';
  constructor(
    private userService: UserService,
    private translateService: TranslateService,
    private contactService: ContactService,
    private modalService: NgbModal,
    private loaderService: LoaderService,
    private formBuilder: UntypedFormBuilder,
    private atlasToast: AtlasToastService,
    private router: Router,
    private route: ActivatedRoute,
    private emailCampaignListService: EmailCampaignListService
  ) {}

  ngOnInit() {
    this.firstColumnDefinition();
    this.getEmailCampaignList();

    if (this.page) {
      this.bulkOption = [];
    } else {
      this.bulkOption = [
        {
          label: 'contacts_creategroup',
          onClick: () => {
            this.openModalAddRemoveCreateGroup('create');
          }
        },
        {
          label: 'contacts_addtogroup',
          onClick: () => {
            this.openModalAddRemoveCreateGroup('add');
          }
        },
        {
          label: 'contacts_list_removefromgroup',
          onClick: () => {
            this.openModalAddRemoveCreateGroup('remove');
          }
        },
        {
          label: 'contacts_list_subscribeservices',
          onClick: () => {
            this.openContactsToEmailCampaign('subscribe');
          }
        },
        {
          label: 'contacts_list_unsubscribeservices',
          onClick: () => {
            this.openContactsToEmailCampaign('unsubscribe');
          }
        },
        {
          label: 'contacts_export_modal',
          onClick: () => {
            this.openModalExport();
          }
        },
        {
          label: 'common_delete',
          onClick: () => {
            this.openModalDeleteMultiple();
          }
        }
      ];
    }
    const that = this;
    this.enableEmailInExport = enableEmailInExport();
    if (this.enableEmailInExport) {
      this.exportForm = this.formBuilder.group({
        email: ['', [Validators.required, Validators.email]],
        format: ['XLS', [Validators.required]]
      });
    } else {
      this.exportForm = this.formBuilder.group({
        format: ['XLS', [Validators.required]]
      });
    }

    this.createGroupForm = this.formBuilder.group({
      name: ['', [Validators.required]],
      description: ['', []]
    });

    this.userService.getAuthenticationState().subscribe(next => (this.user = next));
    const joinCalls: Array<Observable<any>> = [];

    this.generateOverlay();

    joinCalls.push(this.contactService.getFields());
    joinCalls.push(this.contactService.getGroups(0, 1000));
    forkJoin(joinCalls)
      // .pipe(finalize(() => this.loaderService.hide()))
      .subscribe(calls => {
        const fields = calls[0];

        const groups = calls[1];

        let tableFields = this.mapFields(fields);

        tableFields = tableFields.concat([
          {
            headerNameTranslate: 'common_actions',
            suppressCellFocus: true,
            cellRenderer: 'buttonGroupRenderer',
            hide: this.page === 'send' || this.page === 'mail' ? true : false,
            cellRendererParams: {
              hide: contact => {
                if (!contact) {
                  return false;
                } else {
                  if (contact.shared) {
                    return true;
                  } else {
                    return false;
                  }
                }
              },
              items: [
                {
                  onClick: e => this.router.navigate(['/contacts/edit'], { queryParams: { id: e.idContact } }),
                  label: 'common_edit'
                  // variant: 'primary',
                },
                {
                  onClick: e => this.openModalDelete(e),
                  label: 'common_delete'
                  // variant: 'danger',
                }
                // {
                //   onClick: e => this.router.navigate(['/sms/send'], { queryParams: { idContact: e.idContact } }),
                //   label: 'send_sms'
                //   // variant: 'danger',
                // }
                // {
                //   onClick: e => this.view(e),
                //   label: 'list_table_action_view'
                //   // variant: 'primary',
                // }
              ]
            },

            cellClass: ['button-group', 'text-end'],
            maxWidth: 82,
            field: '__actions'
          }
        ]);
        this.tableColumnDefs = tableFields;

        this.tableGridOptions = AtlasGridOptions.deserialize({
          onRowSelected: (event: RowSelectedEvent) => {
            if (event.data.shared && event.node.isSelected() && (this.page !== 'send' || this.page === 'mail')) {
              event.node.setSelected(false);
            } else {
              this.grid.selectionChange.emit(event);
            }
          },
          onGridReady: (event: GridReadyEvent) => {
            this.gridAnimationState = 'show';
            this.grid.refresh();
          },
          onFilterChange: (event: any) => {
            if (event?.type === 'group') {
              this.grid.filter();
            }
          },
          suppressRowClickSelection: true,
          pagination: true,
          columnCustomizationKey: 'contacts-list',
          paginationPageSize: this.paginationPageSize,
          loadFunction: this.reloadList.bind(that),
          filters: this.getFilters(groups),
          filtersAdvanced: this.getFiltersAdvanced(fields, groups),
          getApi: api => {
            // If coming from groups page the group may already be filtered
            this.route.queryParams.subscribe(query => {
              const selectedGroup = query.group;
              if (selectedGroup) {
                const filterGroups = this.tableGridOptions.filters.find(filter => filter.name === 'id-group');
                const selectedGroupObj = filterGroups.options.find(group => group.value === selectedGroup);
                if (selectedGroupObj) {
                  this.grid.filtersModel['id-group'] = selectedGroupObj;
                  this.grid.filtersOpen();
                }
              }
            });
          },
          bulkOptions: this.bulkOption
        });
      });

    this.tableColumnDefs = null;

    // this.reloadList();
  }

  generateOverlay() {
    this.loaderService.resetOverlayHeight();
    this.loaderService.show();
  }

  getEmailCampaignList() {
    this.emailCampaignListService
      .getListCampaignEmail()
      .pipe(
        map((data: EmailCampaign[]) => {
          const newData = data
            .filter(item => item.status === 'ACTIVE' || item.status === 'ARCHIVED')
            .map(item => {
              return {
                label: item.title,
                value: item.idCampaign
              };
            });

          return newData;
        })
      )
      .subscribe(list => {
        this.emailCampaignList = list;
      });
  }

  openModalSuccessExport(): void {
    this.modalSuccessExportModal = this.modalService.open(this.modalSuccessExport);
    this.modalSuccessExportModal.result.then(
      result => {
        this.modalService.dismissAll();
      },
      reason => {
        this.modalService.dismissAll();
      }
    );
  }

  mapFields(fields): Array<any> {
    const defaultShow = this.page === 'mail' ? ['email', 'name', 'surname'] : ['phone', 'email', 'name', 'surname'];
    const tableFields = [];

    fields.data.map(f => {
      const sortables = ['NAME', 'SURNAME'];
      tableFields.push({
        headerNameTranslate: f.label,
        field: f.idField === 'IS_MALE' ? 'gender' : f.idField.toLowerCase(),
        colPrefKey: f.idField === 'IS_MALE' ? 'gender' : f.idField.toLowerCase(),
        suppressCellFocus: true,
        hide: !defaultShow.includes(f.idField.toLowerCase()),
        sortable: sortables.includes(f.idField) ? true : false,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params?.data) {
              if (f.idField === 'IS_MALE') {
                let translation = '';
                if (params.data.gender) {
                  const keyTranslate = params.data.gender === 'm' ? 'contact_male' : 'contact_female';
                  this.translateService.get(keyTranslate).subscribe(label => {
                    translation = label;
                  });
                }
                return translation;
              }
              return params.data[f.idField.toLowerCase()];
            }
          }
        }
      });
    });

    if (this.user.account.subaccount) {
      tableFields.unshift(this.subAccountFirstColumns[1]);
      tableFields.unshift(this.subAccountFirstColumns[0]);
    } else {
      tableFields.unshift(this.superAccountFirstColumn);
    }
    return tableFields;
  }

  firstColumnDefinition() {
    this.superAccountFirstColumn = {
      headerName: '#',
      field: '__sel',
      width: 60,
      headerComponent: GridHeaderSelectComponent,
      cellRenderer: CheckboxCellComponent,
      cellRendererParams: {
        page: this.page ? this.page : 'contacts',
        selectedList: () => this.selectedList
      },
      sortable: false,
      suppressCellFocus: true,
      resizable: false,
      suppressSizeToFit: true
    };

    this.subAccountFirstColumns = [
      {
        headerName: '#',
        field: '__sel',
        width: 60,
        headerComponent: GridHeaderSelectComponent,
        cellRenderer: CheckboxCellComponent,
        cellRendererParams: {
          page: this.page ? this.page : 'contacts'
        },
        suppressCellFocus: true,
        cellStyle: params =>
          params.data?.shared === 'true' && (this.page !== 'send' || this.page !== 'mail') ? { 'pointer-events': 'none' } : '',
        sortable: false,
        resizable: false,
        suppressSizeToFit: true
      },
      {
        headerNameTranslate: 'contacts_list_share_title',
        field: 'shared',
        colPrefKey: 'shared',
        sortable: false,
        suppressCellFocus: true,
        cellRenderer: 'htmlRenderer',
        cellRendererParams: {
          content: params => {
            if (params && params.data) {
              if (params.data.shared) {
                this.translateService.get('common_shared').subscribe(label => {
                  this.badgeLabel = label;
                });
                return '<span class="badge badge-shared">' + this.badgeLabel + '</span>';
              } else {
                return '';
              }
            } else {
              return '';
            }
          }
        }
      }
    ];
  }

  getApiGetContactsOptions() {
    const apiGetContactsOptions = new ApiGetContactsOptions();
    const basicFilters = {};
    ['name', 'surname', 'email', 'phone'].map(x => {
      basicFilters[x] = this.grid.filtersModel[x];
    });
    if (this.grid.filtersModel['id-group']) {
      basicFilters['id-group'] = this.grid.filtersModel['id-group'].value;
    }
    apiGetContactsOptions['basic-filters'] = basicFilters;

    const advancedFilters = this.grid.filtersAdvancedApplied
      ? this.grid.filtersAdvancedModel.map(x => {
          return {
            'id-filter': x.filter.id,
            type:
              x.filter.type === 'CAMPAIGN'
                ? 'CAMPAIGN'
                : x.filter.type === 'DATE'
                ? 'DATE'
                : x.operation.value === 'IN' || x.operation.value === 'OT'
                ? 'GROUP'
                : 'STRING',
            condition: x.operation.value,
            value: x.value
          };
        })
      : null;
    apiGetContactsOptions.filters = advancedFilters;
    return apiGetContactsOptions;
  }

  reloadList(startRow?, endRow?, sort?) {
    if (!this.isLoaderActive) {
      this.generateOverlay();
      this.isLoaderActive = true;
    }
    return this.contactService
      .getContacts(
        1 + startRow / (endRow - startRow),
        endRow - startRow,
        sort && sort.length ? sort[0].colId : null,
        sort && sort.length ? (sort[0].sort === 'desc' ? 'd' : 'a') : null,
        this.getApiGetContactsOptions()
      )
      .pipe(
        finalize(() => {
          this.loaderService.hide();
          this.isLoaderActive = false;
        })
      );
  }

  get showHelpAndShop(): boolean {
    return !this.userService.isSubaccount();
  }

  get getUserName(): string {
    if (this.user) {
      return this.user.profile.name;
    }
    return null;
  }

  openModalDelete(data: any): void {
    this.contactToDelete = data;
    this.modalDeleteModal = this.modalService.open(this.modalDelete);
    this.modalDeleteModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalDeleteForm(): void {
    const that = this;
    this.contactService.deleteContacts([this.contactToDelete]).subscribe(
      () => {
        this.atlasToast.success('contact_delete_success', null);
        that.grid.refresh();
      },
      () => {
        this.atlasToast.error('common_error', null);
        that.grid.refresh();
      },
      () => {}
    );
    this.modalDeleteModal.close();
  }

  ngOnDestroy(): void {
    this.contactService.selectedList.clear();
  }

  getFilters(groups) {
    return [
      {
        label: 'common_group',
        name: 'id-group',
        type: 'dropdown',
        options: [
          {
            label: 'contacts_table_all',
            value: '',
            translate: true,
            type: 'group'
          },
          {
            label: 'contacts_table_none',
            value: 0,
            translate: true,
            type: 'group'
          },
          ...groups.data.map(x => {
            return {
              label: x.name + ' (' + x.totalContactsCount + ')',
              value: x.id,
              type: 'group'
            };
          })
        ]
      },
      {
        name: 'name',
        label: 'contact_name',
        type: 'text'
      },
      {
        name: 'surname',
        label: 'contact_surname',
        type: 'text'
      },
      {
        name: 'email',
        label: 'contact_email',
        type: 'text'
      },
      {
        name: 'phone',
        label: 'contact_phone',
        type: 'text'
      }
    ];
  }

  getFiltersAdvanced(fields, groups) {
    const fieldsList = fields.data.map((field: ContactField) => {
      if (field.idField === 'IS_MALE') {
        return {
          label: 'contact_gender',
          id: field.idField,
          type: 'DROPDOWN',
          translate: true,
          options: [
            {
              label: 'contact_male',
              value: 'true',
              translate: true
            },
            {
              label: 'contact_female',
              value: 'false',
              translate: true
            }
          ]
        };
      }
      return {
        label: field.label,
        id: field.idField,
        type: field.dataType,
        translate: !field.personalizable
      };
    });
    fieldsList.push({
      label: 'contact_domain',
      id: 'contact_domain',
      type: 'STRING',
      translate: true
    });
    fieldsList.push({
      label: 'Lista Email',
      id: 'CAMPAIGN',
      type: 'CAMPAIGN',
      translate: true
    });
    fieldsList.push({
      label: 'common_group',
      id: 'GROUPS',
      type: 'CUSTOMDROPDOWN',
      translate: true,
      operations: [
        {
          label: 'tables_advfilter_cond_equal',
          value: 'IN'
        },
        {
          label: 'tables_advfilter_cond_not_equal',
          value: 'OT'
        }
      ],
      options: groups.data.map((group: ContactGroup) => {
        return {
          label: group.name,
          value: group.id
        };
      })
    });

    return fieldsList;
  }

  // Delete multiple
  openModalDeleteMultiple(): void {
    this.modalDeleteMultipleModal = this.modalService.open(this.modalDeleteMultiple);
    this.modalDeleteMultipleModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalDeleteMultipleForm(): void {
    const that = this;
    that.grid.getSelectedList(false).subscribe((x: any) => {
      that.grid.showLoading();
      this.contactService.deleteContacts(x, that.getApiGetContactsOptions()).subscribe(z => {
        that.grid.deselectAll();
        that.contactService.getGroups(0, 1000).subscribe(groupslist => (this.tableGridOptions.filters = this.getFilters(groupslist)));
        that.grid.refresh();
        this.atlasToast.success('common_success', null);
      });
    });
    this.modalDeleteMultipleModal.close();
  }

  // Export
  openModalExport(): void {
    this.modalExportModal = this.modalService.open(this.modalExport);
    this.modalExportModal.result.then(
      result => {},
      reason => {}
    );
  }

  modalExportForm(): void {
    if (!this.exportForm.valid) {
      validateFormGroup(this.exportForm);
      return;
    }
    this.exportForm.disable();

    this.grid.getSelectedList(false).subscribe((x: any) => {
      // this.grid.showLoading();
      this.contactService
        .exportContacts(
          this.enableEmailInExport ? this.exportForm.get('email').value : '',
          this.exportForm.get('format').value,
          x,
          this.getApiGetContactsOptions()
        )
        .subscribe(z => {
          this.grid.deselectAll();
          // this.grid.refresh();
          this.modalExportModal.close();
          this.exportForm.reset();
          this.exportForm.enable();
          this.modalService.dismissAll();
          this.openModalSuccessExport();
        });
    });
  }

  // Group multiple
  openModalAddRemoveCreateGroup(operation): void {
    this.groupOperation = operation;
    this.tableGroupColumnDefs = [
      {
        headerNameTranslate: 'contacts_group_name',
        field: 'name',
        suppressCellFocus: true
      },
      {
        headerNameTranslate: 'common_description',
        field: 'description',
        suppressCellFocus: true
      },
      {
        headerNameTranslate: 'common_contacts',
        field: 'totalContactsCount',
        suppressCellFocus: true
      }
    ];
    const that = this;
    this.tableGroupGridOptions = AtlasGridOptions.deserialize({
      onGridReady: (event: GridReadyEvent) => {
        this.gridAnimationStateGroup = 'show';
      },
      onRowSelected: (event: RowSelectedEvent) => {
        if (event.data.sharingInfo && event.node.isSelected() && (this.page !== 'send' || this.page === 'mail')) {
          event.node.setSelected(false);
        } else {
          this.groupgrid.selectionChange.emit(event);
          // this.grid.selectionChange.emit(event);
        }
      },
      pagination: true,
      paginationPageSize: this.paginationPageSize,
      loadFunction: this.reloadGroupList.bind(that),
      enableSelect: true,
      getApi: api => (this.tableGroupGridApi = api)
    });

    this.modalAddRemoveCreateGroupModal = this.modalService.open(this.modalAddRemoveCreateGroup);
    this.modalAddRemoveCreateGroupModal.result.then(
      result => {},
      reason => {}
    );
  }

  // associate/dissociate contacts from email campaign
  openContactsToEmailCampaign(operation: string) {
    const options: NgbModalOptions = {
      windowClass: 'custom-campaign-assocation-modal-width'
    };
    const associateContactsToEmailCampaign = this.modalService.open(SelectEmailCampaignModalComponent, options);
    (associateContactsToEmailCampaign.componentInstance as SelectEmailCampaignModalComponent).unsubscribe =
      operation === 'unsubscribe' ? true : false;
    associateContactsToEmailCampaign.result.then(
      result => {
        this.generateBodyAssociateContactsToEmailCampaign(result);
      },
      reason => {}
    );
  }

  generateBodyAssociateContactsToEmailCampaign(data) {
    let body;
    if (this.grid.isSelectedAll()) {
      body = { ...data, search: this.getApiGetContactsOptions() };
    } else {
      const that = this;
      that.grid.getSelectedList(false).subscribe((contacts: any[]) => {
        const idContacts = contacts.map(contact => contact.idContact);
        body = { ...data, idContacts };
      });
    }
    return this.associateDissociateContactsToEmailCampaign(body);
  }

  associateDissociateContactsToEmailCampaign(body) {
    this.contactService.updateContactsSubscription(body).subscribe(
      () => {
        this.atlasToast.success('common_success', null);
        this.router.navigateByUrl(this.router.url);
      },
      error => {
        this.atlasToast.error('common_error', null);
      }
    );
  }

  modalAddRemoveCreateGroupForm(): void {
    const that = this;
    if (this.groupOperation !== 'create') {
      // Get group ids
      this.tableGroupGridApi.gridOptionsWrapper.gridOptions.atlasGrid.getSelectedList().subscribe((groups: any) => {
        this.grid.getSelectedList(false).subscribe((contacts: any) => {
          // this.grid.showLoading();

          const joinCalls: Array<Observable<any>> = [];
          const op =
            this.groupOperation === 'add'
              ? this.contactService.addContactsGroup.bind(this.contactService)
              : this.contactService.removeContactsGroup.bind(this.contactService);
          // groups.map(group => {
          //   joinCalls.push(op(group, contacts, that.getApiGetContactsOptions()));
          // });
          joinCalls.push(op(groups, contacts, that.getApiGetContactsOptions()));
          this.modalAddRemoveCreateGroupModal.close();
          this.grid.deselectAll();
          this.grid.showLoading();
          forkJoin(joinCalls).subscribe(calls => {
            // const x = calls[0];
            this.atlasToast.success('common_success', null);
            this.contactService.getGroups(0, 1000).subscribe(groupslist => (this.tableGridOptions.filters = this.getFilters(groupslist)));
            this.grid.refresh();
            // this.grid.hideLoading();
          });
        });
      });
    } else {
      if (!this.createGroupForm.valid) {
        validateFormGroup(this.createGroupForm);
        return;
      }
      this.createGroupForm.disable();
      const raw = ContactGroup.deserialize(this.createGroupForm.getRawValue());
      this.grid.showLoading();
      this.contactService.addGroup(ContactGroup.deserialize(raw)).subscribe(
        group => {
          this.createGroupForm.reset();
          this.createGroupForm.enable();
          this.modalAddRemoveCreateGroupModal.close();
          this.grid.getSelectedList(false).subscribe((contacts: any) => {
            this.grid.deselectAll();
            // this.grid.showLoading();

            this.contactService.addContactsGroup(group, contacts, that.getApiGetContactsOptions()).subscribe(calls => {
              // const x = calls[0];
              this.atlasToast.success('common_success', null);
              this.grid.hideLoading();
            });
            this.contactService.getGroups(0, 1000).subscribe(groups => (this.tableGridOptions.filters = this.getFilters(groups)));
          });
        },
        () => {
          that.grid.deselectAll();
          this.grid.hideLoading();
          this.atlasToast.error('common_error', null);
        },
        () => {}
      );
    }
  }

  reloadGroupList(startRow?, endRow?, sort?) {
    return this.contactService.searchGroups(
      this.applyFilterForAddOrRemoveContactbyGroup(),
      1 + startRow / (endRow - startRow),
      endRow - startRow
    );
  }

  private applyFilterForAddOrRemoveContactbyGroup(): ContactGropsSearchFilter {
    const filter = new ContactGropsSearchFilter();
    filter.countContacts = true;
    filter.onlyOwnedGroups = true;
    return filter;
  }

  showSelected() {}

  goToCreateContact() {
    this.router.navigate(['contacts/create']);
  }

  searchEvent(event) {
    // console.log(this.contactService.selectedList);
  }
}
